import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface Testimonial {
  id: string;
  customer_name: string;
  location: string;
  rating: number;
  comment: string;
  source: string;
  is_active: boolean;
  is_featured: boolean;
  created_at: string;
  updated_at: string;
}

export const useTestimonials = () => {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const { data, error } = await supabase
        .from('testimonials')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching testimonials:', error);
        return;
      }

      setTestimonials(data || []);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    } finally {
      setLoading(false);
    }
  };

  const addTestimonial = async (testimonial: Omit<Testimonial, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error } = await supabase
        .from('testimonials')
        .insert([testimonial])
        .select()
        .single();

      if (error) {
        console.error('Error adding testimonial:', error);
        return { success: false, error };
      }

      await fetchTestimonials();
      return { success: true, data };
    } catch (error) {
      console.error('Error adding testimonial:', error);
      return { success: false, error };
    }
  };

  const updateTestimonial = async (id: string, updates: Partial<Testimonial>) => {
    try {
      const { data, error } = await supabase
        .from('testimonials')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating testimonial:', error);
        return { success: false, error };
      }

      await fetchTestimonials();
      return { success: true, data };
    } catch (error) {
      console.error('Error updating testimonial:', error);
      return { success: false, error };
    }
  };

  const deleteTestimonial = async (id: string) => {
    try {
      const { error } = await supabase
        .from('testimonials')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting testimonial:', error);
        return { success: false, error };
      }

      await fetchTestimonials();
      return { success: true };
    } catch (error) {
      console.error('Error deleting testimonial:', error);
      return { success: false, error };
    }
  };

  const getFeaturedTestimonials = () => {
    return testimonials.filter(testimonial => testimonial.is_featured);
  };

  return { 
    testimonials, 
    loading, 
    refetch: fetchTestimonials,
    addTestimonial,
    updateTestimonial,
    deleteTestimonial,
    getFeaturedTestimonials
  };
};